import React, { useEffect, useMemo, useState } from "react";
import { X } from "lucide-react";

/* Preferred display ORDER + labels for known countable features. The keys that
   actually render are read from each subscription document (key sets vary per
   plan); this list only controls ordering + labels, and acts as a fallback when
   a subscription has no countable_features at all. */
export const COUNTABLE_FEATURE_KEYS = [
  "resume_screening_count",
  "job_roles_count",
  "interview_count",
  "interview_count_v2",
  "create_jd_v1",
  "create_jd_v2",
  "bulk_upload",
  "hr_count",
];

export const COUNTABLE_LABELS = {
  resume_screening_count: "Resume Screening",
  job_roles_count: "Job Roles",
  interview_count: "AI Interviews",
  interview_count_v2: "Interactive Interviews",
  create_jd_v1: "Job Roles (Smart)",
  create_jd_v2: "Job Roles (Custom)",
  bulk_upload: "Bulk Uploads",
  hr_count: "HR Accounts",
};

/* Preferred ORDER + labels for known boolean features. Rendered keys come from
   the subscription document; this is the fallback set when none are present. */
export const DEFAULT_BOOLEAN_KEYS = [
  "resume_screening_enabled",
  "ats_enabled",
  "create_jd_v1_enabled",
  "create_jd_v2_enabled",
  "candidate_form_enabled",
  "mcq_enabled",
  "coding_enabled",
  "typing_enabled",
  "behavioral_enabled",
  "hr_creation_enabled",
  "video_included",
  "storing_enabled",
  "secondary_camera_enabled",
  "tab_switch_screenshot",
  "interactivity_enabled",
  "live_avatar_enabled",
  "assembly_tts_enabled",
  "interview_v1_animation",
  "report_sharing",
];

export const BOOLEAN_LABELS = {
  resume_screening_enabled: "Resume Screening",
  ats_enabled: "ATS Integration",
  create_jd_v1_enabled: "Smart Mode JD",
  create_jd_v2_enabled: "Builder Mode JD",
  candidate_form_enabled: "Candidate Form",
  mcq_enabled: "MCQ Assessments",
  coding_enabled: "Coding Assessments",
  typing_enabled: "Typing Tests",
  behavioral_enabled: "Behavioral Assessments",
  hr_creation_enabled: "Recruiter Seats",
  video_included: "Video Proctoring",
  storing_enabled: "Recording Storage",
  secondary_camera_enabled: "Secondary Camera",
  tab_switch_screenshot: "Tab Security",
  interactivity_enabled: "Interactive Chat",
  live_avatar_enabled: "Live Avatar",
  assembly_tts_enabled: "Assembly TTS",
  interview_v1_animation: "Dynamic UI Animation",
  report_sharing: "Report Sharing",
};

/* Interview scheduling budgets are DERIVED (interview count + 20%) and managed by
   the backend — never entered directly, so they are hidden from the editable inputs. */
export const DERIVED_COUNTABLE_KEYS = new Set([
  "interview_scheduled_count",
  "interview_scheduled_count_v2",
]);

/* Real interview counts that get a derived scheduling budget shown alongside them. */
const INTERVIEW_COUNT_KEYS = new Set(["interview_count", "interview_count_v2"]);

/* Mirror of backend scheduled_from_count: round-nearest, half up; keep negatives. */
export const scheduledFromCount = (n) => {
  const c = Number(n) || 0;
  return c < 0 ? c : Math.round(c * 1.2);
};

const prettify = (key) =>
  key
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

const boolLabel = (key) => BOOLEAN_LABELS[key] || prettify(key);

const toDateInput = (value) => {
  if (!value) return "";
  const str = String(value);
  // handles ISO strings and "YYYY-MM-DD ..." variants
  const datePart = str.split(/[ T]/)[0];
  if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) return datePart;
  const d = new Date(str);
  if (isNaN(d.getTime())) return "";
  return d.toISOString().split("T")[0];
};

const todayInput = () => new Date().toISOString().split("T")[0];

const MODAL_CARD_STYLE = {
  background:
    "linear-gradient(to bottom right, rgba(34,52,97,0.25), rgba(11,10,31,0.05))",
  backdropFilter: "blur(40px)",
  WebkitBackdropFilter: "blur(50px)",
  borderRadius: "10px",
};

/* Small toggle switch */
const Toggle = ({ checked, onChange }) => (
  <button
    type="button"
    onClick={() => onChange(!checked)}
    className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
      checked ? "bg-gradient-to-r from-pink-500 to-blue-500" : "bg-gray-600/60"
    }`}
  >
    <span
      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
        checked ? "translate-x-6" : "translate-x-1"
      }`}
    />
  </button>
);

const ModalShell = ({ title, onClose, children, footer }) => (
  <div className="fixed inset-0 flex items-center justify-center z-[99999999]">
    <div
      className="absolute inset-0 bg-black bg-opacity-50"
      style={{ backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)" }}
      onClick={onClose}
    />
    <div
      className="w-full max-w-lg p-6 rounded-lg shadow-lg relative mx-auto overflow-y-auto max-h-[80vh]"
      style={MODAL_CARD_STYLE}
    >
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold text-white">{title}</h2>
        <button
          onClick={onClose}
          className="p-1.5 rounded-md hover:bg-slate-700 text-gray-400 hover:text-gray-300 transition-colors"
        >
          <X className="text-red-400 hover:text-red-300 w-5 h-5" />
        </button>
      </div>
      {children}
      {footer}
    </div>
  </div>
);

/* Resolve keys/values from a subscription's granted_features */
const useGrantedFeatures = (subscription) =>
  useMemo(() => {
    const granted = subscription?.granted_features || {};
    const countable = granted.countable_features || {};
    const boolean = granted.boolean_features || {};
    const addons = subscription?.custom_addons || {};

    // Render exactly the keys the subscription document carries (key sets vary
    // per plan). Order known keys by preference, append any unknown extras.
    // Fall back to the full known list only when the document has nothing.
    const presentCountable = new Set(
      [...Object.keys(countable), ...Object.keys(addons)].filter((k) => !DERIVED_COUNTABLE_KEYS.has(k))
    );
    let countableKeys;
    if (presentCountable.size) {
      const ordered = COUNTABLE_FEATURE_KEYS.filter((k) => presentCountable.has(k));
      const extras = [...presentCountable].filter((k) => !COUNTABLE_FEATURE_KEYS.includes(k));
      countableKeys = [...ordered, ...extras];
    } else {
      countableKeys = COUNTABLE_FEATURE_KEYS;
    }

    const presentBoolean = Object.keys(boolean);
    let booleanKeys;
    if (presentBoolean.length) {
      const ordered = DEFAULT_BOOLEAN_KEYS.filter((k) => k in boolean);
      const extras = presentBoolean.filter((k) => !DEFAULT_BOOLEAN_KEYS.includes(k));
      booleanKeys = [...ordered, ...extras];
    } else {
      booleanKeys = DEFAULT_BOOLEAN_KEYS;
    }

    return { countable, boolean, addons, countableKeys, booleanKeys };
  }, [subscription]);

/* ============================ EXTEND PLAN MODAL ============================ */
export const ExtendPlanModal = ({ isOpen, onClose, subscription, onSubmit, submitting }) => {
  const { countable, boolean, addons, countableKeys, booleanKeys } =
    useGrantedFeatures(subscription);

  const [endDate, setEndDate] = useState("");
  const [addonInputs, setAddonInputs] = useState({});
  const [boolInputs, setBoolInputs] = useState({});
  const [error, setError] = useState("");

  useEffect(() => {
    if (!isOpen) return;
    setEndDate(toDateInput(subscription?.end_date));
    setAddonInputs({});
    const initialBools = {};
    booleanKeys.forEach((k) => {
      initialBools[k] = !!boolean[k];
    });
    setBoolInputs(initialBools);
    setError("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, subscription]);

  if (!isOpen) return null;

  const handleAddonChange = (key, value) => {
    if (value !== "" && !/^\d+$/.test(value)) return;
    setAddonInputs((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = () => {
    if (!endDate) {
      setError("Please select an end date.");
      return;
    }
    const addonsPayload = {};
    Object.entries(addonInputs).forEach(([key, val]) => {
      const num = parseInt(val, 10);
      if (num > 0) addonsPayload[key] = num;
    });
    onSubmit({
      end_date: endDate,
      addons: addonsPayload,
      boolean_features: boolInputs,
    });
  };

  return (
    <ModalShell
      title="Extend Plan"
      onClose={onClose}
      footer={
        <div className="flex justify-end gap-4 mt-6">
          <button
            onClick={onClose}
            className="px-8 py-2 border border-gray-600 text-[#CCCCCC] rounded-full hover:bg-gray-800/50 transition text-sm"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="px-8 py-2 bg-gradient-to-r from-pink-500 to-blue-500 text-white rounded-full transition text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {submitting && (
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
            )}
            {submitting ? "Extending..." : "Extend"}
          </button>
        </div>
      }
    >
      <p className="text-sm text-gray-400 mb-4">
        Plan: <span className="text-white font-medium">{subscription?.plan_display_name || subscription?.plan_name || "N/A"}</span>. Extend the
        duration and/or grant extra counts (add-ons) on the same plan.
      </p>

      <div className="mb-5">
        <label className="block text-gray-300 mb-2 text-sm">New End Date</label>
        <input
          type="date"
          value={endDate}
          min={todayInput()}
          onChange={(e) => setEndDate(e.target.value)}
          className="w-full px-3 py-2 bg-transparent border border-gray-700/50 rounded text-[#CCCCCC] focus:outline-none [color-scheme:dark]"
        />
      </div>

      <div className="mb-5">
        <h3 className="text-[#D3B4FF] text-sm font-medium mb-3">Add-on Counts</h3>
        <div className="space-y-3">
          {countableKeys.map((key) => {
            const original = Number(countable[key] || 0);
            const existingAddon = Number(addons[key] || 0);
            const pendingAddon = parseInt(addonInputs[key], 10) || 0;
            const newTotal = original + existingAddon + pendingAddon;
            return (
              <div key={key} className="flex items-center justify-between gap-3">
                <div className="flex flex-col">
                  <span className="text-sm text-white">{COUNTABLE_LABELS[key] || prettify(key)}</span>
                  <span className="text-xs text-gray-400">
                    Current: {original + existingAddon}
                    {existingAddon > 0 ? ` (${original} + ${existingAddon} add-on)` : ""}
                  </span>
                  {INTERVIEW_COUNT_KEYS.has(key) && (
                    <span className="text-xs text-[#8fb0ff]">
                      Scheduling budget: {scheduledFromCount(newTotal)} (+20%)
                    </span>
                  )}
                </div>
                <input
                  type="text"
                  inputMode="numeric"
                  placeholder="+ Add"
                  value={addonInputs[key] ?? ""}
                  onChange={(e) => handleAddonChange(key, e.target.value)}
                  className="w-24 px-3 py-1.5 bg-transparent border border-gray-700/50 rounded text-[#CCCCCC] placeholder-gray-500 text-sm text-right focus:outline-none"
                />
              </div>
            );
          })}
        </div>
      </div>

      <div className="mb-2">
        <h3 className="text-[#D3B4FF] text-sm font-medium mb-3">Features</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
          {booleanKeys.map((key) => (
            <div key={key} className="flex items-center justify-between gap-3">
              <span className="text-sm text-gray-200">{boolLabel(key)}</span>
              <Toggle
                checked={!!boolInputs[key]}
                onChange={(val) => setBoolInputs((prev) => ({ ...prev, [key]: val }))}
              />
            </div>
          ))}
        </div>
      </div>

      {error && <p className="text-red-400 text-sm mt-4">{error}</p>}
    </ModalShell>
  );
};

/* ========================= CONVERT TO PAID MODAL ========================= */
export const ConvertToPaidModal = ({ isOpen, onClose, subscription, onSubmit, submitting }) => {
  const { countable, boolean, addons, countableKeys, booleanKeys } =
    useGrantedFeatures(subscription);

  const [endDate, setEndDate] = useState("");
  const [countInputs, setCountInputs] = useState({});
  const [boolInputs, setBoolInputs] = useState({});
  const [error, setError] = useState("");

  useEffect(() => {
    if (!isOpen) return;
    setEndDate("");
    const initialCounts = {};
    (countableKeys.length ? countableKeys : COUNTABLE_FEATURE_KEYS).forEach((k) => {
      const base = Number(countable[k] || 0) + Number(addons[k] || 0);
      initialCounts[k] = String(base);
    });
    setCountInputs(initialCounts);
    const initialBools = {};
    booleanKeys.forEach((k) => {
      initialBools[k] = !!boolean[k];
    });
    setBoolInputs(initialBools);
    setError("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, subscription]);

  if (!isOpen) return null;

  const keysToRender = countableKeys.length ? countableKeys : COUNTABLE_FEATURE_KEYS;

  const handleCountChange = (key, value) => {
    if (value !== "" && !/^\d+$/.test(value)) return;
    setCountInputs((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = () => {
    if (!endDate) {
      setError("Please select an end date.");
      return;
    }
    const countable_features = {};
    keysToRender.forEach((key) => {
      countable_features[key] = parseInt(countInputs[key], 10) || 0;
    });
    onSubmit({
      end_date: endDate,
      countable_features,
      boolean_features: boolInputs,
    });
  };

  return (
    <ModalShell
      title="Convert to Paid"
      onClose={onClose}
      footer={
        <div className="flex justify-end gap-4 mt-6">
          <button
            onClick={onClose}
            className="px-8 py-2 border border-gray-600 text-[#CCCCCC] rounded-full hover:bg-gray-800/50 transition text-sm"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="px-8 py-2 bg-gradient-to-r from-pink-500 to-blue-500 text-white rounded-full transition text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {submitting && (
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
            )}
            {submitting ? "Converting..." : "Convert"}
          </button>
        </div>
      }
    >
      <p className="text-sm text-gray-400 mb-4">
        This creates a new <span className="text-white font-medium">Paid</span> subscription with the
        configuration below. The current subscription becomes inactive.
      </p>

      <div className="mb-5">
        <label className="block text-gray-300 mb-2 text-sm">End Date</label>
        <input
          type="date"
          value={endDate}
          min={todayInput()}
          onChange={(e) => setEndDate(e.target.value)}
          className="w-full px-3 py-2 bg-transparent border border-gray-700/50 rounded text-[#CCCCCC] focus:outline-none [color-scheme:dark]"
        />
      </div>

      <div className="mb-5">
        <h3 className="text-[#D3B4FF] text-sm font-medium mb-3">Counts</h3>
        <div className="space-y-3">
          {keysToRender.map((key) => (
            <div key={key} className="flex items-center justify-between gap-3">
              <div className="flex flex-col">
                <span className="text-sm text-white">{COUNTABLE_LABELS[key] || prettify(key)}</span>
                {INTERVIEW_COUNT_KEYS.has(key) && (
                  <span className="text-xs text-[#8fb0ff]">
                    Scheduling budget: {scheduledFromCount(countInputs[key])} (+20%)
                  </span>
                )}
              </div>
              <input
                type="text"
                inputMode="numeric"
                value={countInputs[key] ?? ""}
                onChange={(e) => handleCountChange(key, e.target.value)}
                className="w-24 px-3 py-1.5 bg-transparent border border-gray-700/50 rounded text-[#CCCCCC] text-sm text-right focus:outline-none"
              />
            </div>
          ))}
        </div>
      </div>

      <div className="mb-2">
        <h3 className="text-[#D3B4FF] text-sm font-medium mb-3">Features</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
          {booleanKeys.map((key) => (
            <div key={key} className="flex items-center justify-between gap-3">
              <span className="text-sm text-gray-200">{boolLabel(key)}</span>
              <Toggle
                checked={!!boolInputs[key]}
                onChange={(val) => setBoolInputs((prev) => ({ ...prev, [key]: val }))}
              />
            </div>
          ))}
        </div>
      </div>

      {error && <p className="text-red-400 text-sm mt-4">{error}</p>}
    </ModalShell>
  );
};
